
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { updateUserProfile } from "@/services/auth/userProfileService";
import { CNPJInput, PhoneInput } from "@/components/ui/masked-inputs";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useOnboarding } from "../OnboardingProvider";

const formSchema = z.object({
  document: z.string().regex(/^\d{2}\.\d{3}\.\d{3}\/\d{4}\-\d{2}$/, {
    message: "CNPJ deve estar no formato XX.XXX.XXX/XXXX-XX"
  }),
  phone: z.string().regex(/^\(\d{2}\) \d{5}\-\d{4}$/, {
    message: "Telefone deve estar no formato (XX) XXXXX-XXXX"
  }),
  address: z.string().min(5, "Endereço deve ter pelo menos 5 caracteres"),
});

type FormValues = z.infer<typeof formSchema>;

interface ProfileStepProps {
  onNext: () => void;
}

export function ProfileStep({ onNext }: ProfileStepProps) {
  const { user, updateUserInContext } = useAuth();
  const { completeStep } = useOnboarding();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      document: user?.document || '',
      phone: user?.phone || '',
      address: user?.address || '',
    }
  });

  const onSubmit = async (values: FormValues) => {
    if (!user) return;

    setIsSubmitting(true);
    
    try {
      const updatedUser = await updateUserProfile(user.id, {
        document: values.document,
        phone: values.phone,
        address: values.address,
      });

      if (updatedUser && updateUserInContext) {
        updateUserInContext(updatedUser);
      }

      completeStep('profile');
      
      toast({
        title: "Perfil atualizado!",
        description: "Suas informações foram salvas com sucesso.",
      });

      onNext();
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message || "Não foi possível atualizar o perfil",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold mb-2">Complete seu perfil</h2>
        <p className="text-muted-foreground">
          Precisamos de algumas informações básicas para configurar sua conta.
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="document"
            render={({ field }) => (
              <FormItem>
                <FormLabel>CNPJ</FormLabel>
                <FormControl>
                  <CNPJInput
                    {...field}
                    disabled={isSubmitting}
                    className="w-full"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Telefone</FormLabel>
                <FormControl>
                  <PhoneInput
                    {...field}
                    disabled={isSubmitting}
                    className="w-full"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Endereço</FormLabel>
                <FormControl>
                  <Textarea 
                    {...field}
                    disabled={isSubmitting}
                    placeholder="Endereço completo da empresa"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button 
            type="submit" 
            className="w-full" 
            disabled={isSubmitting}
          >
            {isSubmitting ? "Salvando..." : "Continuar"}
          </Button>
        </form>
      </Form>
    </div>
  );
}
